/*!
 * Black Dashboard PRO React - v1.0.0
 * Product Page: https://www.creative-tim.com/product/black-dashboard-pro-react
 * Copyright 2019 Creative Tim (https://www.creative-tim.com)
 * Coded by Creative Tim
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';

import { ApolloClient } from 'apollo-client';
import { HttpLink } from 'apollo-link-http';
import { split } from 'apollo-link';
import { ApolloLink, Observable } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { getMainDefinition } from 'apollo-utilities';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloProvider } from 'react-apollo';
import { createGraphqlWsLink } from '../../Auth/graphqlWsLink';
import getRoles from '../../Auth/getRoles';

import { adminRoutes, merchantRoutes, pickerRoutes, inventoryManagerRoutes } from 'routes.js';
import * as shipment_urls from '../../urls/api-shipments/admin';
import * as user_urls from '../../urls/api-users/admin';
import * as crm_urls from '../../urls/api-shipments/crm';

import axios from 'axios';
import config from '../../config';

import AdminNavbar from 'components/Global/AdminNavbar';
import Footer from 'components/Global/Footer';
import Sidebar from 'components/Global/Sidebar';
import { ThemeContext } from '../../contexts/ThemeContext';
import {
  SESSION_EXPIRED_EVENT,
  SESSION_EXPIRED_REASON_DEFAULT,
  buildAuthorizationHeader,
  clearStoredSession,
  decodeJwtPayload,
  getStoredIdToken,
  hasActiveSession,
  hasStoredSessionTokens,
  notifySessionExpired,
  renewSessionSilently,
  shouldRenewSessionSoon
} from '../../Auth/session';
import { DASHBOARD_PERF_MARKS, flushDashboardPerformance, markDashboardPerformance, measureDashboardPerformance } from '../../utils/dashboardPerformance';
import { getRealtimeTransportPolicy, logDashboardEnvironmentSummary } from '../../utils/environmentMode';
// import MarketsSelection from './MarketsSelection';

const isSubscriptionOperation = ({ query }) => {
  const definition = getMainDefinition(query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};

const SESSION_RECOVERED_EVENT = 'gophr:session-recovered';

const createWebSocketLink = uri => {
  const realtimePolicy = getRealtimeTransportPolicy(uri);

  return createGraphqlWsLink({
    url: uri,
    retryAttempts: realtimePolicy.reconnectionAttempts,
    connectionParams: async () => {
      // Ensure a fresh token before the WS handshake so subscriptions (metrics,
      // completed orders) don't authenticate with a stale token on hard refresh,
      // which leaves realtime connections dead until a full re-login.
      if (!hasActiveSession() || shouldRenewSessionSoon()) {
        try {
          await renewSessionSilently();
        } catch (renewError) {
          // Renewal failed (e.g. session has no refresh token). Don't loop forever on an
          // expired token — surface session expiry so the app does a clean re-login.
          if (!hasActiveSession()) {
            notifySessionExpired({ reason: 'ws_token_expired' });
          }
        }
      }
      return {
        headers: {
          Authorization: buildAuthorizationHeader()
        }
      };
    }
  });
};

class Admin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeColor: 'red',
      sidebarMini: true,
      opacity: 0,
      role: null,
      routes: null,
      pictureUrl: '',
      hideSidebar: true,
      sidebarOpened: false,
      sessionExpired: false,
      sessionMessage: ''
    };

    this.axiosInterceptorId = null;
    this.axiosRequestInterceptorId = null;
    this.sessionRedirectTimeout = null;
    this.sessionRecoveryPromise = null;
    this.mainPanelRef = React.createRef();

    this.httpLink_ship = new HttpLink({
      uri: shipment_urls.hasuraHost
    });

    this.wssLink_ship = createWebSocketLink(shipment_urls.wssHasuraHost);

    this.httpLink_user = new HttpLink({
      uri: user_urls.hasuraHost
    });

    this.wssLink_user = createWebSocketLink(user_urls.wssHasuraHost);

    this.authLink = new ApolloLink((operation, forward) => {
      // Never BLOCK a request on token renewal. The WebSocket path always sends the current
      // token and works; the HTTP authLink used to await renewSessionSilently when the local
      // session looked expired, and if that renewal didn't resolve promptly the request was
      // never sent -> queries hung on "Loading..." forever until a hard refresh. Instead:
      // forward immediately with the current token and renew in the BACKGROUND. A genuinely
      // expired token that 401s is handled by apolloErrorLink -> attemptSessionRecovery (refetch).
      return new Observable(observer => {
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            Authorization: buildAuthorizationHeader()
          }
        }));

        if (!hasActiveSession() || shouldRenewSessionSoon()) {
          renewSessionSilently().catch(() => null);
        }

        const subscription = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer)
        });

        return () => {
          subscription.unsubscribe();
        };
      });
    });

    this.apolloErrorLink = onError(({ graphQLErrors, networkError }) => {
      if (this.isApolloUnauthorized(graphQLErrors, networkError)) {
        this.attemptSessionRecovery().catch(() => {
          // Only force logout when the token is genuinely expired and could not be renewed
          // (an expired token is unusable). A still-valid token survives transient renew blips.
          if (!hasActiveSession()) {
            notifySessionExpired({ reason: 'apollo_unauthorized' });
          }
        });
      }
    });

    this.shipmentsLink = split(isSubscriptionOperation, this.wssLink_ship, this.authLink.concat(this.httpLink_ship));
    this.usersLink = split(isSubscriptionOperation, this.wssLink_user, this.authLink.concat(this.httpLink_user));

    this.link = ApolloLink.from([
      this.apolloErrorLink,
      split(operation => operation.getContext().clientName === 'userClient', this.usersLink, this.shipmentsLink)
    ]);

    this.shipmentsClient = new ApolloClient({
      link: this.link,
      cache: new InMemoryCache()
    });
  }

  isApolloUnauthorized = (graphQLErrors, networkError) => {
    if (networkError && (networkError.statusCode === 401 || networkError.statusCode === 403)) {
      return true;
    }

    if (!graphQLErrors || graphQLErrors.length === 0) {
      return false;
    }

    return graphQLErrors.some(error => {
      const message = error && error.message ? error.message.toLowerCase() : '';
      const code = error && error.extensions && error.extensions.code ? String(error.extensions.code).toLowerCase() : '';

      return (
        message.includes('unauthorized') ||
        message.includes('jwt') ||
        message.includes('invalid-jwt') ||
        code === 'access-denied' ||
        code === 'invalid-jwt'
      );
    });
  };

  handleSessionExpired = reason => {
    if (this.state.sessionExpired) {
      return;
    }

    const sessionMessage = reason || 'Your session expired. Redirecting to login...';
    clearStoredSession();
    markDashboardPerformance(DASHBOARD_PERF_MARKS.SESSION_EXPIRED, {
      reason: reason || SESSION_EXPIRED_REASON_DEFAULT
    });

    this.clearSessionExpiryTimeout();

    // Permanently close realtime sockets on logout so they don't reconnect with a dead session.
    if (this.wssLink_ship && this.wssLink_ship.wsClient) {
      this.wssLink_ship.wsClient.dispose();
    }

    if (this.wssLink_user && this.wssLink_user.wsClient) {
      this.wssLink_user.wsClient.dispose();
    }

    this.shipmentsClient.clearStore().catch(() => null);
    flushDashboardPerformance('session_expired');

    this.setState({ sessionExpired: true, sessionMessage }, () => {
      this.sessionRedirectTimeout = setTimeout(() => {
        this.props.history.replace('/logout');
      }, 250);
    });
  };

  handleSessionExpiredEvent = event => {
    const detail = event && event.detail ? event.detail : {};
    this.handleSessionExpired(detail.message || 'Your session expired. Redirecting to login...');
  };

  clearSessionExpiryTimeout = () => {
    if (this.sessionRedirectTimeout !== null) {
      clearTimeout(this.sessionRedirectTimeout);
      this.sessionRedirectTimeout = null;
    }
  };

  refreshRealtimeConnections = () => {
    // Abruptly terminate the sockets so graphql-ws reconnects and re-runs connectionParams
    // with the freshly renewed token (subscriptions resubscribe automatically).
    if (this.wssLink_ship && this.wssLink_ship.wsClient) {
      this.wssLink_ship.wsClient.terminate();
    }

    if (this.wssLink_user && this.wssLink_user.wsClient) {
      this.wssLink_user.wsClient.terminate();
    }
  };

  attemptSessionRecovery = () => {
    if (this.sessionRecoveryPromise) {
      return this.sessionRecoveryPromise;
    }

    if (!hasStoredSessionTokens()) {
      return Promise.reject(new Error('No stored session tokens available.'));
    }

    this.sessionRecoveryPromise = renewSessionSilently()
      .then(() => {
        this.refreshRealtimeConnections();
        return this.shipmentsClient.resetStore()
          .catch(() => this.shipmentsClient.reFetchObservableQueries().catch(() => null))
          .then(() => {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent(SESSION_RECOVERED_EVENT));
            }

            return null;
          });
      })
      .finally(() => {
        this.sessionRecoveryPromise = null;
      });

    return this.sessionRecoveryPromise;
  };

  setSidebarOpen = isOpen => {
    this.setState({ sidebarOpened: isOpen });
    document.documentElement.classList.toggle('nav-open', isOpen);
  };

  shouldLogoutOnUnauthorized = error => {
    const responseStatus = error && error.response ? error.response.status : null;
    if (responseStatus !== 401) {
      return false;
    }

    const expiresAtRaw = window.localStorage.getItem('expires_at');
    const expiresAt = expiresAtRaw ? Number(JSON.parse(expiresAtRaw)) : NaN;
    const sessionExpired = Number.isFinite(expiresAt) && Date.now() >= expiresAt;

    if (sessionExpired) {
      return !hasStoredSessionTokens();
    }

    const requestUrl = error && error.config && typeof error.config.url === 'string'
      ? error.config.url
      : '';
    const isLocalServiceRequest = /^http:\/\/localhost:(8081|8082|8083|8084|8085)\//.test(requestUrl);

    if (isLocalServiceRequest) {
      return false;
    }

    return true;
  };

  initializeAxiosInterceptor = () => {
    if (this.axiosInterceptorId !== null || this.axiosRequestInterceptorId !== null) {
      return;
    }

    this.axiosRequestInterceptorId = axios.interceptors.request.use(
      requestConfig => {
        const nextConfig = requestConfig || {};
        const requestUrl = typeof nextConfig.url === 'string' ? nextConfig.url : '';
        const isApiRequest =
          requestUrl.startsWith('/')
          || requestUrl.startsWith('https://api-')
          || requestUrl.startsWith('http://localhost:808')
          || requestUrl.startsWith('https://localhost:808')
          || requestUrl.startsWith('http://127.0.0.1:808')
          || requestUrl.startsWith('https://127.0.0.1:808');

        if (!isApiRequest) {
          return nextConfig;
        }

        const existingAuthHeader =
          nextConfig.headers && (nextConfig.headers.Authorization || nextConfig.headers.authorization);

        if (existingAuthHeader) {
          return nextConfig;
        }

        const authorizationHeader = buildAuthorizationHeader();

        if (!authorizationHeader) {
          return nextConfig;
        }

        return {
          ...nextConfig,
          headers: {
            ...(nextConfig.headers || {}),
            Authorization: authorizationHeader,
          },
        };
      },
      error => Promise.reject(error)
    );

    this.axiosInterceptorId = axios.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        const responseStatus = error && error.response ? error.response.status : null;
        const originalRequest = error && error.config ? error.config : null;

        if ((responseStatus === 401 || responseStatus === 403) && originalRequest && !originalRequest._sessionRetry) {
          originalRequest._sessionRetry = true;

          return this.attemptSessionRecovery()
            .then(() => axios({
              ...originalRequest,
              headers: {
                ...(originalRequest.headers || {}),
                Authorization: buildAuthorizationHeader()
              }
            }))
            .catch(() => {
              if (this.shouldLogoutOnUnauthorized(error) && !hasStoredSessionTokens()) {
                notifySessionExpired({ reason: 'axios_unauthorized' });
              }

              return Promise.reject(error);
            });
        }

        if (this.shouldLogoutOnUnauthorized(error) && !hasStoredSessionTokens()) {
          notifySessionExpired({ reason: 'axios_unauthorized' });
        }
        return Promise.reject(error);
      }
    );
  };

  componentDidMount() {
    markDashboardPerformance(DASHBOARD_PERF_MARKS.ADMIN_MOUNTED, {
      path: this.props.location && this.props.location.pathname ? this.props.location.pathname : ''
    });
    logDashboardEnvironmentSummary(config.config);
    document.documentElement.classList.remove('nav-open');
    this.initializeAxiosInterceptor();
    window.addEventListener(SESSION_EXPIRED_EVENT, this.handleSessionExpiredEvent);

    if (!hasActiveSession()) {
      if (!hasStoredSessionTokens()) {
        this.handleSessionExpired('Your session expired. Redirecting to login...');
        return;
      }

      this.attemptSessionRecovery().catch(() => {
        if (!hasActiveSession()) {
          this.handleSessionExpired('Your session expired. Redirecting to login...');
        }
      });
    }

    getRoles().then(val => {
      var sidebar = true;
      if (val === 'error') {
        if (!hasStoredSessionTokens()) {
          this.handleSessionExpired('Your session expired. Redirecting to login...');
        }
        return;
      } else {
        const roleValue = Array.isArray(val) ? val[0] : val;
        const roleNumber = Number(roleValue);
        const resolvedRole = Number.isFinite(roleNumber) ? roleNumber : roleValue;
        var r = null;
        if (resolvedRole === 0 || resolvedRole === 100) {
          sidebar = false;
          r = adminRoutes;
        } else if (resolvedRole === 200 || resolvedRole === 220) {
          sidebar = false;
          r = merchantRoutes;
        } else if (resolvedRole === 230) {
          sidebar = false;
          r = inventoryManagerRoutes;
        } else if (resolvedRole === 240) {
          sidebar = false;
          r = pickerRoutes;
        } else if (Number.isFinite(roleNumber) && roleNumber > 240) {
          // ! may show something else later
          sidebar = false;
          r = pickerRoutes;
        }
        const idToken = getStoredIdToken();
        if (!idToken) {
          this.handleSessionExpired('Your session expired. Redirecting to login...');
          return;
        }

        const id_token_decoded = decodeJwtPayload(idToken);
        if (!id_token_decoded) {
          this.handleSessionExpired('Your session expired. Redirecting to login...');
          return;
        }

        const finishRouteInitialization = (accountingAllowed, crmAllowed) => {
          const accessibleRoutes = Array.isArray(r)
            ? r.filter(route =>
                (!route.requiresAccountingAccess || accountingAllowed) &&
                (!route.requiresCrmAccess || crmAllowed)
              )
            : r;

          markDashboardPerformance(DASHBOARD_PERF_MARKS.ROLES_READY, {
            role: resolvedRole
          });
          measureDashboardPerformance('login_to_roles_ready_ms', DASHBOARD_PERF_MARKS.LOGIN_AUTHENTICATED, DASHBOARD_PERF_MARKS.ROLES_READY);
          measureDashboardPerformance('admin_mount_to_roles_ready_ms', DASHBOARD_PERF_MARKS.ADMIN_MOUNTED, DASHBOARD_PERF_MARKS.ROLES_READY);
          this.setState({ role: resolvedRole, routes: accessibleRoutes, hideSidebar: sidebar, pictureUrl: id_token_decoded.picture });
        };

        if (r === adminRoutes) {
          const accountingCheck = axios
            .get(user_urls.accountingAccess)
            .then(result => {
              const payload = result && result.data ? result.data.payload : null;
              return Boolean(payload && payload.allowed);
            })
            .catch(() => false);
          const crmCheck = axios
            .get(crm_urls.crmAccess)
            .then(result => {
              const payload = result && result.data ? result.data.payload : null;
              return Boolean(payload && payload.allowed);
            })
            .catch(() => false);
          Promise.all([accountingCheck, crmCheck]).then(([accountingAllowed, crmAllowed]) =>
            finishRouteInitialization(accountingAllowed, crmAllowed || crm_urls.isCrmLocalPreview())
          );
        } else {
          finishRouteInitialization(false, false);
        }
      }
    });
    if (navigator.platform.indexOf('Win') > -1) {
      document.documentElement.className += ' perfect-scrollbar-on';
      document.documentElement.classList.remove('perfect-scrollbar-off');
      if (this.mainPanelRef.current) {
        new PerfectScrollbar(this.mainPanelRef.current);
      }
      let tables = document.querySelectorAll('.table-responsive');
      for (let i = 0; i < tables.length; i++) {
        new PerfectScrollbar(tables[i]);
      }
    }
    window.addEventListener('scroll', this.showNavbarButton);
  }

  componentWillUnmount() {
    document.documentElement.classList.remove('nav-open');

    if (this.axiosRequestInterceptorId !== null) {
      axios.interceptors.request.eject(this.axiosRequestInterceptorId);
      this.axiosRequestInterceptorId = null;
    }

    if (this.axiosInterceptorId !== null) {
      axios.interceptors.response.eject(this.axiosInterceptorId);
      this.axiosInterceptorId = null;
    }

    this.clearSessionExpiryTimeout();

    window.removeEventListener(SESSION_EXPIRED_EVENT, this.handleSessionExpiredEvent);

    if (this.wssLink_ship && this.wssLink_ship.wsClient) {
      this.wssLink_ship.wsClient.dispose();
    }

    if (this.wssLink_user && this.wssLink_user.wsClient) {
      this.wssLink_user.wsClient.dispose();
    }

    if (navigator.platform.indexOf('Win') > -1) {
      document.documentElement.className += ' perfect-scrollbar-off';
      document.documentElement.classList.remove('perfect-scrollbar-on');
    }
    window.removeEventListener('scroll', this.showNavbarButton);
  }

  componentDidUpdate(e) {
    if (e.location.pathname !== e.history.location.pathname) {
      if (this.state.sidebarOpened) {
        this.setSidebarOpen(false);
      }

      if (navigator.platform.indexOf('Win') > -1) {
        let tables = document.querySelectorAll('.table-responsive');
        for (let i = 0; i < tables.length; i++) {
          new PerfectScrollbar(tables[i]);
        }
      }
      document.documentElement.scrollTop = 0;
      document.scrollingElement.scrollTop = 0;
      if (this.mainPanelRef.current) {
        this.mainPanelRef.current.scrollTop = 0;
      }
    }
  }

  // showNavbarButton = () => {
  //   if (document.documentElement.scrollTop > 50 || document.scrollingElement.scrollTop > 50 || this.refs.mainPanel.scrollTop > 50) {
  //     this.setState({ opacity: 1 });
  //   } else if (document.documentElement.scrollTop <= 50 || document.scrollingElement.scrollTop <= 50 || this.refs.mainPanel.scrollTop <= 50) {
  //     this.setState({ opacity: 0 });
  //   }
  // };

  showNavbarButton = () => {
    const panelScrollTop = this.mainPanelRef.current ? this.mainPanelRef.current.scrollTop : 0;
    const scrollTop = document.documentElement.scrollTop || document.scrollingElement.scrollTop || panelScrollTop;
    const newOpacity = scrollTop > 50 ? 1 : 0;

    if (newOpacity !== this.state.opacity) {
      this.setState({ opacity: newOpacity });
    }
  };

  getRoutes = routes => {
    if (!Array.isArray(routes) || routes.length === 0) {
      return null;
    }

    return routes.map((prop, key) => {
      if (prop.collapse) {
        return this.getRoutes(prop.views);
      }
      if (prop.layout === '/admin') {
        const routePath = prop.layout + prop.path;
        return <Route key={routePath || key} path={routePath} render={props => <prop.component {...props} role={this.state.role} />} />;
      } else {
        return null;
      }
    }).flat();
  };

  getActiveRoute = routes => {
    let activeRoute = 'Gophr Dashboard';

    if (!Array.isArray(routes) || routes.length === 0) {
      return activeRoute;
    }

    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = this.getActiveRoute(routes[i].views);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        if (this.props.location.pathname.indexOf(routes[i].layout + routes[i].path) !== -1) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };

  handleActiveClick = color => {
    this.setState({ activeColor: color });
  };

  handleMiniClick = () => {
    if (document.body.classList.contains('sidebar-mini')) {
      this.setState({ sidebarMini: false });
    } else {
      this.setState({ sidebarMini: true });
    }
    document.body.classList.toggle('sidebar-mini');
  };

  toggleSidebar = () => {
    this.setSidebarOpen(!this.state.sidebarOpened);
  };

  closeSidebar = () => {
    this.setSidebarOpen(false);
  };

  render() {
    const { isDarkMode } = this.context;

    if (this.state.role === null && !this.state.sessionExpired) {
      return (
        <div className={`wrapper${isDarkMode ? '' : ' white-content'}`}>
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isDarkMode ? '#ffffff' : '#212529',
              fontSize: '1.1rem'
            }}
          >
            Loading dashboard...
          </div>
        </div>
      );
    }

    if (this.state.role !== null && (!Array.isArray(this.state.routes) || this.state.routes.length === 0)) {
      return (
        <div className={`wrapper${isDarkMode ? '' : ' white-content'}`}>
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              color: isDarkMode ? '#ffffff' : '#212529'
            }}
          >
            <div style={{ maxWidth: '520px', textAlign: 'center' }}>
              <h4 style={{ marginBottom: '12px' }}>Dashboard access unavailable</h4>
              <p style={{ marginBottom: 0 }}>
                Your session loaded, but no dashboard routes were available for this account.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <ApolloProvider client={this.shipmentsClient}>
        <div className={`wrapper${isDarkMode ? '' : ' white-content'}`}>
          {this.state.sessionExpired && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(17, 25, 40, 0.72)',
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px'
              }}
            >
              <div
                style={{
                  maxWidth: '420px',
                  width: '100%',
                  background: isDarkMode ? '#27293d' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#212529',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  boxShadow: '0 18px 45px rgba(0, 0, 0, 0.25)'
                }}
              >
                <h4 style={{ marginBottom: '12px' }}>Session expired</h4>
                <p style={{ marginBottom: 0 }}>{this.state.sessionMessage || 'Redirecting to login...'}</p>
              </div>
            </div>
          )}
          {this.state.role !== null ? (
            <>
              {!this.state.hideSidebar && (
                <div>
                  <div className="navbar-minimize-fixed" style={{ opacity: this.state.opacity }}>
                    <button className="minimize-sidebar btn btn-link btn-just-icon" onClick={this.handleMiniClick}>
                      <i className="tim-icons icon-align-center visible-on-sidebar-regular text-muted" style={{ color: '#f15a29' }} />
                      <i className="tim-icons icon-bullet-list-67 visible-on-sidebar-mini text-muted" style={{ color: '#f15a29' }} />
                    </button>
                  </div>
                  <Sidebar {...this.props} routes={this.state.routes} activeColor={this.state.activeColor} closeSidebar={this.closeSidebar} />
                </div>
              )}
              {/* <div>
                <MarketsSelection />
              </div> */}
              <div className="main-panel" ref={this.mainPanelRef} data={this.state.activeColor}>
                <AdminNavbar
                  {...this.props}
                  handleMiniClick={this.handleMiniClick}
                  brandText={this.getActiveRoute(this.state.routes)}
                  sidebarOpened={this.state.sidebarOpened}
                  toggleSidebar={this.toggleSidebar}
                  hideSidebar={this.state.hideSidebar}
                  pictureUrl={this.state.pictureUrl}
                />
                <Switch>
                  {this.getRoutes(this.state.routes)}
                  <Redirect exact from="/admin" to="/admin/dashboard" />
                  <Redirect to="/admin/dashboard" />
                </Switch>
                <Footer />
              </div>
            </>
          ) : (
            <div
              style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                color: isDarkMode ? '#ffffff' : '#212529'
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <h4 style={{ marginBottom: '12px' }}>Loading dashboard...</h4>
                <p style={{ marginBottom: 0 }}>Preparing your account and permissions.</p>
              </div>
            </div>
          )}
        </div>
      </ApolloProvider>
    );
  }
}

Admin.contextType = ThemeContext;

export default Admin;
